/**
 * Book Now Widget
 *
 * This Alpine JS function powers the Book Now widget, the template for which can be found at
 * templates/_components/bookingWidget.twig.
 *
 * @param {Object} initialData - Initial data for the booking widget.
 * @param {string} [initialData.startDate] - The default start date for the booking.
 * @param {number} [initialData.duration] - The default duration of the booking in days.
 * @param {Object} [initialData.roomConfiguration] - The room and guests configuration details.
 */

(function () {
  const locale = 'en-US';

  const bookingDateStorage = {
    getItem(key) {  
      const storedValue = window.localStorage.getItem(key);
      if (!storedValue) return null;

      const dateValue = new Date(storedValue);
      const isStaleDate = dateValue < new Date();
      if (isStaleDate) return null;

      return storedValue;
    },
    setItem(key, value) {
      window.localStorage.setItem(key, value);
    }
  }

  window.pluralizeWithCount = (word, count, pluralForm = `${word}s`) => {
    return `${count} ${count === 1 ? word : pluralForm}`;
  }

  document.addEventListener('alpine:init', () => {
    const fields = {
      DATE_RANGE: 'dateRange',
      ROOMS_AND_GUESTS: 'roomsAndGuests',
      PROMOCODE: 'promocode',
    }

    Alpine.data('bookingWidgetData', function (initialData = {}) {
      const {
        siteName,
        startDate: initialStartDate,
        duration,
        roomConfiguration: {
          maxRooms,
          maxGuestsPerRoom,
          guestCategories,
        },
        globalPromocode,
      } = initialData || {}

      const startDate = initialStartDate ? new Date(initialStartDate) : new Date();
      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + duration);

      // Returns an object in the format { adults: 2, children: 0, infants: 0 }
      const getDefaultGuests = () => {
        return guestCategories.reduce((acc, item) => {
          acc[item.category] = item.defaultCount || 0;
          return acc;
        }, {});
      }

      return {
        activeField: null,
        showBookingForm: false,
        fields,
        isTablet: false,
        isCalendarInitialised: false,
        
        persistentStartDate: this.$persist(startDate.toISOString().split('T')[0])
          .as(`${siteName}_booking_startDate`)
          .using(bookingDateStorage),
        persistentEndDate: this.$persist(endDate.toISOString().split('T')[0])
          .as(`${siteName}_booking_endDate`)
          .using(bookingDateStorage),

        maxRoomsCount: maxRooms,
        maxGuestsPerRoom,
        rooms: this.$persist([getDefaultGuests()]).as(`${siteName}_booking_rooms`),
        persistedPromocode: this.$persist(null).as(`${siteName}_booking_promocode`),
        globalPromocode: globalPromocode || null,
        isPromocodeTouched: false,

        get promocodeFieldValue() {
          return this.persistedPromocode !== null
            ? this.persistedPromocode
            : this.isPromocodeTouched
              ? ''
              : this.globalPromocode ?? '';
        },

        // Computed properties (derived values) are defined as getters, not actions.
        get startDate() {
          return new Date(this.persistentStartDate);
        },

        get endDate() {
          return new Date(this.persistentEndDate);
        },

        get duration() {
          return Math.ceil((this.endDate - this.startDate) / (1000 * 60 * 60 * 24));
        },

        /**
         * summarySelectedStartDate - Returns a formatted start date string.
         *
         * Logic:
         * - If the start date is in the current year:
         *     - Display weekday, month, date with suffix on tablets and mobile devices.
         * 
         * - If the start date is not in the current year:
         *     - Display weekday, month, date with suffix, and year on tablets and wide screens.
         *     - Display month, date with suffix, and year on mobile screens.
         *
         * Examples:
         *
         * 1. Current year, non-tablet or tablet:
         *    startDate = Oct 01, 2025
         *    current year = 2025
         *    → "Wednesday, 1 Oct"
         *
         * 2. Different year, tablet and wide screens:
         *    isTablet  = true
         *    startDate = Jan 01, 2026
         *    current year = 2025
         *    → "Thursday, 1 Jan 2026"
         *
         * 3. Different year, mobile screens:
         *    isTablet  = false
         *    startDate = Jan 01, 2026
         *    current year = 2025
         *    → "1 Jan 2026"
         */
        get summarySelectedStartDate() {
          const isCurrentYear = this.startDate.getFullYear() === new Date().getFullYear();
          const weekday = this.startDate.toLocaleDateString(locale, { weekday: 'long', });
          const month = this.startDate.toLocaleDateString(locale, { month: 'short' });
          const dateMonth = `${this.startDate.getDate()} ${month}`; 
          const year = this.startDate.getFullYear();

          if (isCurrentYear) return `${weekday}, ${dateMonth}`;
          if (this.isTablet) return `${weekday}, ${dateMonth} ${year}`;
          return `${dateMonth} ${year}`; 
        },
  
        /**
         * formButtonSelectedDateRange - Returns a formatted date range string.
         *
         * Logic:
         * - Displays long month names on tablet and wide screens, short month names on smaller devices.
         * 
         * - If start and end dates are in the same year:
         *   - Start date omits the year.
         *   - End date includes the year.
         * 
         * - If start and end dates are in different years:
         *   - Both start and end dates include the year.
         *
         * Examples:
         *
         * 1. Same year on tablets and wide screens
         *    isTablet  = true
         *    startDate = Oct 01, 2025
         *    endDate   = Oct 07, 2025
         *    → "Wed, October 01 - Tue, October 07, 2025"
         *
         * 2. Same year on mobile screens
         *    isTablet  = false
         *    startDate = Oct 01, 2025
         *    endDate   = Oct 07, 2025
         *    → "Wed, Oct 01 - Tue, October 07, 2025"
         *
         * 3. Different years on tablets and wide screens
         *    isTablet  = true
         *    startDate = Dec 30, 2025
         *    endDate   = Jan 02, 2026
         *    → "Tue, December 30, 2025 - Fri, January 02, 2026"
         * 
         * 4. Different years on mobile screens
         *    isTablet  = false
         *    startDate = Dec 30, 2025
         *    endDate   = Jan 02, 2026
         *    → "Tue, Dec 30, 2025 - Fri, Jan 02, 2026"
        */
        get formButtonSelectedDateRange() {
          const isSameYear = this.startDate.getFullYear() === this.endDate.getFullYear();
          const defaultOptions = {
            weekday: 'short',
            day: '2-digit',
            month: this.isTablet ? 'long' : 'short'
          };
          const startDateOptions = {
            ...defaultOptions,
            ...(isSameYear ? {} : { year: 'numeric' })
          };
          const endDateOptions = { ...defaultOptions, year: 'numeric' };

          return `${this.startDate.toLocaleDateString(locale, startDateOptions)} - ${this.endDate.toLocaleDateString(locale, endDateOptions)}`;
        },

        get formButtonSelectedRoomsGuests() {
          return `${pluralizeWithCount('Room', this.rooms.length)}, ${pluralizeWithCount('guest', this.totalGuestsCount)}`;
        },

        get totalGuestsCount() {
          return this.rooms.reduce((sum, r) => {
            const roomTotal = Object.values(r).reduce((total, value) => total + value, 0);
            return sum + roomTotal;
          }, 0);
        },

        // UI event handlers that update booking form state and active field selection.
        showDatesSelectionHandler: {
          ['x-on:click']() {
            this.showBookingForm = true;
            this.activeField = this.fields.DATE_RANGE;
          },
        },

        showRoomsAndGuestsHandler: {
          ['x-on:click']() {
            this.showBookingForm = true;
            this.activeField = this.fields.ROOMS_AND_GUESTS;
          },
        },

        bookingDatesClickHandler: {
          ['x-on:click']() {
            this.activeField = this.fields.DATE_RANGE;
          },
        },

        bookingRoomsGuestsClickHandler: {
          ['x-on:click']() {
            this.activeField = this.fields.ROOMS_AND_GUESTS;
          },
        },
  
        closeBookingFormHandler: {
          ['x-on:click']() {
            this.showBookingForm = false;
          },
        },

        dialogCloseHandler: {
          ['x-on:close']() {
            this.showBookingForm = false;
          },
        },

        promocodeFocusHandler: {
          ['x-on:focus']() {
            this.activeField = this.fields.PROMOCODE;
          },
        },

        continueBookingHandler: {
          ['x-on:click']() {
            this.activeField = null;
          },
        },

        // Booking form actions
        openBookingForm() {
          this.$refs.bookingDialog.showModal();
          bodyScrollLock['disableBodyScroll'](this.$refs.bookingWidgetContainer, { reserveScrollBarGap: true });
        },
        
        closeBookingForm() {
          this.$refs.bookingDialog.close();
          bodyScrollLock['enableBodyScroll'](this.$refs.bookingWidgetContainer, { reserveScrollBarGap: true });
        },

        handlePromocodeInput(value) {
          this.isPromocodeTouched = true;
          this.persistedPromocode = value.trim() || null;
        },

        // Room management actions
        addRoom() {
          if (this.rooms.length > this.maxRoomsCount) return;
          this.rooms.push(getDefaultGuests());
        },
  
        removeRoom(index) {
          if (this.rooms.length <= 1) return;
          this.rooms.splice(index, 1);
        },

        // Generated with help from ChatGPT
        getMaxAllowed(roomIndex, category) {
          if (!this.rooms[roomIndex]) return 0;

          const room = this.rooms[roomIndex];
          const otherCategoriesTotal = guestCategories
            .filter(item => item.category !== category)
            .reduce((sum, item) => sum + room[item.category], 0);

          const remainingCount = maxGuestsPerRoom - otherCategoriesTotal;
          const currentCategory = guestCategories.find(item => item.category === category);
          const currentCategoryMax = currentCategory?.maxCount || 0;

          return Math.min(currentCategoryMax, remainingCount);
        },

        getArrayOfValues(roomIndex, category) {
          const maxAllowed = this.getMaxAllowed(roomIndex, category);
          return Array.from(Array(maxAllowed + 1).keys());
        },
  
        initCalendar() {
          if (this.isCalendarInitialised) return;
          if (!window.VanillaCalendarPro || !window.VanillaCalendarPro.Calendar) return;
  
          const vanillaCalendarProInstance = new window.VanillaCalendarPro.Calendar(this.$refs.vanillaCalendarProContainer, {
            type: 'multiple',
            displayMonthsCount: 2,
            monthsToSwitch: 1,
            displayDatesOutside: false,
            disableDatesPast: true,
            enableEdgeDatesOnly: true,
            selectionDatesMode: 'multiple-ranged',
            selectedTheme: 'light',
            selectedDates: [this.startDate, this.endDate],
            selectedMonth: this.startDate.getMonth(),
            selectedYear: this.startDate.getFullYear(),
            onClickDate: (self) => {
              if (self.context.selectedDates?.length !== 2) return;              
              this.persistentStartDate = self.context.selectedDates[0];
              this.persistentEndDate = self.context.selectedDates[1];
            },
          });
  
          if (!vanillaCalendarProInstance) return;
          vanillaCalendarProInstance.init();
          this.isCalendarInitialised = true;
        },

        init() {
          this.$watch('showBookingForm', (showBookingForm) => {
            showBookingForm ? this.openBookingForm() : this.closeBookingForm();
          });

          this.$watch('activeField', activeField => activeField === this.fields.DATE_RANGE && this.initCalendar());

          const mq = window.matchMedia('(min-width: 640px)');
          this.isTablet = mq.matches;
          mq.addEventListener('change', e => this.isTablet = e.matches);
        },
      }
    });
  });
})();
